import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Toast } from 'primereact/toast';
import axios from 'axios';

import InsurerSearchDropdown from '../master-certificate/components/InsurerSearchDropdown';
import CreateNewCarriers from '../master-certificate/components/CreateNewCarriers';
import '../../styles/mastercertificate.css';
import '../../styles/pages.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PolicyForm {
  // General
  policyNumber: string;
  effectiveDate: Date | null;
  expirationDate: Date | null;
  bindDate: Date | null;
  binderId: string;
  businessType: number | null;
  businessSubType: string;
  premiumSent: string;
  policyURL: string;
  autoRenew: boolean;
  blanketAI: boolean;
  mortgageeBilled: boolean;
  blanketWS: boolean;
  // Lines of Business
  lineOfBusinessName: string;
  lineOfBusinessId: string;
  // Additional
  carrierName: string;
  mgaName: string;
  billingCompanyName: string;
  packageName: string;
  referralSourceCompanyName: string;
  leadSource: string;
  financeCompanyName: string;
  description: string;
}

const BUSINESS_TYPES = [
  { label: 'New Business', value: 1 },
  { label: 'Renewal',      value: 2 },
  { label: 'Rewrite',      value: 3 },
];

const PREMIUM_SENT_OPTIONS = [
  { label: 'Gross', value: 'Gross' },
  { label: 'Net',   value: 'Net' },
];

type LobOption = { label: string; value: string; id: string };

const fetchLobOptions = async (): Promise<LobOption[]> => {
  const res = await axios.get(
    `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/line-of-business`,
    {
      params: { top: 500, skip: 0 },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
        NCToken: localStorage.getItem('NCToken') || '',
      },
    }
  );
  const items: { name: string; databaseId?: string }[] = Array.isArray(res.data)
    ? res.data
    : (res.data?.value ?? []);
  return items
    .filter((i) => i.name)
    .map((i) => ({
      label: i.name.replace(/\bWorker's Compensation\b/g, "Workers' Compensation"),
      value: i.name,
      id: i.databaseId ?? '',
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const INIT: PolicyForm = {
  policyNumber: '',
  effectiveDate: null,
  expirationDate: null,
  bindDate: null,
  binderId: '',
  businessType: null,
  businessSubType: '',
  premiumSent: 'Gross',
  policyURL: '',
  autoRenew: false,
  blanketAI: false,
  mortgageeBilled: false,
  blanketWS: false,
  lineOfBusinessName: '',
  lineOfBusinessId: '',
  carrierName: '',
  mgaName: '',
  billingCompanyName: '',
  packageName: '',
  referralSourceCompanyName: '',
  leadSource: '',
  financeCompanyName: '',
  description: '',
};

function toApiDate(d: Date | null): string | null {
  if (!d) return null;
  return d.toISOString();
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 500, color: '#0057A4', marginBottom: 5 }}>
      {required && <span style={{ color: '#ba170b', marginRight: 2 }}>*</span>}
      {children}
    </div>
  );
}

function Field({ children, span2 }: { children: React.ReactNode; span2?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gridColumn: span2 ? '1 / -1' : undefined }}>
      {children}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function AddNewPolicyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = React.useRef<Toast>(null);

  const insuredId: string   = (location.state as any)?.insuredId   ?? '';
  const insuredName: string = (location.state as any)?.insuredName ?? '';

  const [form, setForm] = React.useState<PolicyForm>(INIT);
  const [saving, setSaving] = React.useState(false);
  const [errors, setErrors] = React.useState<Partial<Record<keyof PolicyForm, string>>>({});
  const [lobOptions, setLobOptions] = React.useState<LobOption[]>([]);
  const [lobLoading, setLobLoading] = React.useState(true);
  const [showCreateCarrier, setShowCreateCarrier] = React.useState(false);

  React.useEffect(() => {
    fetchLobOptions()
      .then(setLobOptions)
      .catch(() => setLobOptions([]))
      .finally(() => setLobLoading(false));
  }, []);;

  const set = <K extends keyof PolicyForm>(key: K, val: PolicyForm[K]) =>
    setForm((p) => ({ ...p, [key]: val }));

  const validate = (): boolean => {
    const e: typeof errors = {};
    if (!form.policyNumber.trim())   e.policyNumber      = 'Required';
    if (!form.effectiveDate)         e.effectiveDate      = 'Required';
    if (!form.expirationDate)        e.expirationDate     = 'Required';
    if (!form.lineOfBusinessName)    e.lineOfBusinessName = 'Required';
    if (!form.carrierName.trim())    e.carrierName        = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_COIPortal_Url}/api/igigcoi/insert-policy`,
        {
          insuredDatabaseId: insuredId,
          number: form.policyNumber.trim(),
          effectiveDate: toApiDate(form.effectiveDate),
          expirationDate: toApiDate(form.expirationDate),
          lineOfBusinessName: form.lineOfBusinessName,
          lineOfBusinessNames: [form.lineOfBusinessName],
          ...(form.lineOfBusinessId && { lineOfBusinessId: form.lineOfBusinessId }),
          carrierName: form.carrierName.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('igtoken')}`,
            'Content-Type': 'application/json',
            NCToken: localStorage.getItem('NCToken') || '',
          },
        }
      );

      toast.current?.show({
        severity: 'success',
        summary: 'Policy Created Successfully',
        detail: `Policy "${form.policyNumber.trim()}" (${form.lineOfBusinessName}) has been added${insuredName ? ` for ${insuredName}` : ''}. Redirecting...`,
        life: 2500,
      });

      setTimeout(() => navigate('/agentdashboard'), 2000);
    } catch (err: any) {
      const status = (err as any)?.response?.status;
      const serverMsg = (err as any)?.response?.data?.message;

      let summary = 'Failed to Create Policy';
      let detail = 'Something went wrong. Please try again.';

      if (serverMsg) {
        detail = serverMsg;
      } else if (status === 401 || status === 403) {
        detail = 'Your session has expired. Please log in again.';
      } else if (status === 400) {
        detail = 'Invalid policy data. Please check the fields and try again.';
      } else if (status >= 500) {
        detail = 'Server error. Please try again in a moment.';
      }

      toast.current?.show({ severity: 'error', summary, detail, life: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const inp = (key: keyof PolicyForm) => errors[key] ? 'p-invalid' : '';

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="sendCertPage__container">
      <Toast ref={toast} />

      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div style={{ paddingBottom: 16, borderBottom: '1px solid #e6e7ef', marginBottom: 0 }}>
        <div className="masterCert__pageTitle" style={{ fontSize: '1.15rem', textAlign: 'left' }}>
          Add New Policy
        </div>
        {insuredName && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            <i className="pi pi-user" style={{ fontSize: 11, marginRight: 4 }} />
            {insuredName}
          </div>
        )}
      </div>

      {/* ── Scrollable Body ──────────────────────────────────────────────── */}
      <div className="sendCertPage__scroll" style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 20 }}>

        {/* ══ General ══════════════════════════════════════════════════════ */}
        <section className="masterCert__section">
          <div className="masterCert__sectionTitle" style={{ marginBottom: 16 }}>General</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>

            {/* Row 1: Policy Number | Binder ID */}
            <Field>
              <FieldLabel required>Policy Number</FieldLabel>
              <InputText
                value={form.policyNumber}
                onChange={(e) => set('policyNumber', e.target.value)}
                placeholder="Enter policy number"
                className={`masterCert__input ${inp('policyNumber')}`}
                style={{ width: '100%' }}
              />
              {errors.policyNumber && <small className="p-error" style={{ marginTop: 3 }}>{errors.policyNumber}</small>}
            </Field>

            {false && (
            <Field>
              <FieldLabel>Binder ID</FieldLabel>
              <InputText
                value={form.binderId}
                onChange={(e) => set('binderId', e.target.value)}
                placeholder="e.g. 1465"
                className="masterCert__input"
                style={{ width: '100%' }}
              />
            </Field>
            )}

            {/* Row 2: Eff. Date | Exp. Date */}
            <Field>
              <FieldLabel required>Eff. Date</FieldLabel>
              <div className="masterCert__dateCell">
                <Calendar
                  value={form.effectiveDate}
                  onChange={(e) => set('effectiveDate', e.value as Date | null)}
                  dateFormat="mm/dd/yy"
                  showIcon
                  placeholder="MM/DD/YYYY"
                  inputClassName={inp('effectiveDate') || undefined}
                  style={{ width: '100%' }}
                />
              </div>
              {errors.effectiveDate && <small className="p-error" style={{ marginTop: 3 }}>{errors.effectiveDate}</small>}
            </Field>

            <Field>
              <FieldLabel required>Exp. Date</FieldLabel>
              <div className="masterCert__dateCell">
                <Calendar
                  value={form.expirationDate}
                  onChange={(e) => set('expirationDate', e.value as Date | null)}
                  dateFormat="mm/dd/yy"
                  showIcon
                  placeholder="MM/DD/YYYY"
                  inputClassName={inp('expirationDate') || undefined}
                  style={{ width: '100%' }}
                />
              </div>
              {errors.expirationDate && <small className="p-error" style={{ marginTop: 3 }}>{errors.expirationDate}</small>}
            </Field>

            {false && (<>
            {/* Row 3: Bind Date | Business Type */}
            <Field>
              <FieldLabel>Bind Date</FieldLabel>
              <div className="masterCert__dateCell">
                <Calendar
                  value={form.bindDate}
                  onChange={(e) => set('bindDate', e.value as Date | null)}
                  dateFormat="mm/dd/yy"
                  showIcon
                  placeholder="MM/DD/YYYY"
                  style={{ width: '100%' }}
                />
              </div>
            </Field>

            <Field>
              <FieldLabel required>Business Type</FieldLabel>
              <Dropdown
                value={form.businessType}
                options={BUSINESS_TYPES}
                onChange={(e) => set('businessType', e.value)}
                placeholder="[Please choose]"
                className={inp('businessType') || undefined}
                style={{ width: '100%' }}
              />
              {errors.businessType && <small className="p-error" style={{ marginTop: 3 }}>{errors.businessType}</small>}
            </Field>

            {/* Row 4: Business Sub-Type | Premium Sent */}
            <Field>
              <FieldLabel>Business Sub-Type</FieldLabel>
              <InputText
                value={form.businessSubType}
                onChange={(e) => set('businessSubType', e.target.value)}
                placeholder="[Please choose]"
                className="masterCert__input"
                style={{ width: '100%' }}
              />
            </Field>

            <Field>
              <FieldLabel>Premium Sent</FieldLabel>
              <Dropdown
                value={form.premiumSent}
                options={PREMIUM_SENT_OPTIONS}
                onChange={(e) => set('premiumSent', e.value)}
                style={{ width: '100%' }}
              />
            </Field>

            {/* Row 5: Policy URL (full width) */}
            <Field span2>
              <FieldLabel>Policy URL</FieldLabel>
              <InputText
                value={form.policyURL}
                onChange={(e) => set('policyURL', e.target.value)}
                placeholder="https://"
                className="masterCert__input"
                style={{ width: '100%' }}
              />
            </Field>

            {/* Row 6: Checkboxes (full width) */}
            <Field span2>
              <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', paddingTop: 4 }}>
                {([
                  { key: 'autoRenew',      label: 'Auto Renew' },
                  { key: 'blanketAI',      label: 'Blanket AI' },
                  { key: 'mortgageeBilled',label: 'Mortgagee Billed' },
                  { key: 'blanketWS',      label: 'Blanket WS' },
                ] as { key: keyof PolicyForm; label: string }[]).map(({ key, label }) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Checkbox
                      inputId={key}
                      checked={!!form[key]}
                      onChange={(e) => set(key, !!e.checked as any)}
                    />
                    <label htmlFor={key} style={{ fontSize: 13, color: '#0057A4', fontWeight: 500, cursor: 'pointer' }}>
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </Field>
            </>)}
          </div>
        </section>

        {/* ══ Lines of Business ════════════════════════════════════════════ */}
        <section className="masterCert__section">
          <div className="masterCert__sectionTitle" style={{ marginBottom: 16 }}>Lines of Business</div>
          <div style={{ maxWidth: 480 }}>
            <FieldLabel required>Lines of Business</FieldLabel>
            <Dropdown
              value={form.lineOfBusinessName}
              options={lobOptions}
              onChange={(e) => {
                set('lineOfBusinessName', e.value);
                const opt = lobOptions.find((o) => o.value === e.value);
                set('lineOfBusinessId', opt?.id ?? '');
              }}
              placeholder={lobLoading ? 'Loading...' : '[Please choose]'}
              disabled={lobLoading}
              editable
              filter
              filterPlaceholder="Search..."
              className={inp('lineOfBusinessName') || undefined}
              style={{ width: '100%' }}
              panelStyle={{ width: '480px', maxWidth: '480px' }}
            />
            {errors.lineOfBusinessName && <small className="p-error" style={{ marginTop: 3, display: 'block' }}>{errors.lineOfBusinessName}</small>}
          </div>
        </section>

        {/* ══ Carrier ══════════════════════════════════════════════════════ */}
        <section className="masterCert__section">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <div className="masterCert__sectionTitle" style={{ marginBottom: 0 }}>Carrier</div>
            <Button
              label="Add New"
              icon="pi pi-plus"
              outlined
              size="small"
              className="carrierDialog__secondaryBtn"
              style={{ flexShrink: 0, padding: '2px 10px', fontSize: '0.7rem', height: '28px' }}
              onClick={() => setShowCreateCarrier(true)}
            />
          </div>
          <div style={{ maxWidth: 480 }}>
            <FieldLabel required>Carrier</FieldLabel>
            <InsurerSearchDropdown
              value={form.carrierName}
              onChange={(name) => set('carrierName', name)}
              className={`masterCert__input${errors.carrierName ? ' p-invalid' : ''}`}
            />
            {errors.carrierName && <small className="p-error" style={{ marginTop: 3, display: 'block' }}>{errors.carrierName}</small>}
          </div>
        </section>

        {/* ══ Additional ═══════════════════════════════════════════════════ */}
        {/* <section className="masterCert__section">
          <div className="masterCert__sectionTitle" style={{ marginBottom: 16 }}>Additional</div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>

            <Field>
              <FieldLabel>Carrier</FieldLabel>
              <InputText
                value={form.carrierName}
                onChange={(e) => set('carrierName', e.target.value)}
                placeholder="Type and/or choose"
                className="masterCert__input"
                style={{ width: '100%' }}
              />
            </Field>

            <Field>
              <FieldLabel>MGA</FieldLabel>
              <InputText
                value={form.mgaName}
                onChange={(e) => set('mgaName', e.target.value)}
                placeholder="Type and/or choose"
                className="masterCert__input"
                style={{ width: '100%' }}
              />
            </Field>

            <Field>
              <FieldLabel>Billing Company</FieldLabel>
              <InputText
                value={form.billingCompanyName}
                onChange={(e) => set('billingCompanyName', e.target.value)}
                placeholder="Type and/or choose"
                className="masterCert__input"
                style={{ width: '100%' }}
              />
            </Field>

            <Field>
              <FieldLabel>Package</FieldLabel>
              <InputText
                value={form.packageName}
                onChange={(e) => set('packageName', e.target.value)}
                placeholder="Type and/or choose"
                className="masterCert__input"
                style={{ width: '100%' }}
              />
            </Field>

            <Field>
              <FieldLabel>Referral Source</FieldLabel>
              <InputText
                value={form.referralSourceCompanyName}
                onChange={(e) => set('referralSourceCompanyName', e.target.value)}
                placeholder="Type and/or choose"
                className="masterCert__input"
                style={{ width: '100%' }}
              />
            </Field>

            <Field>
              <FieldLabel>Lead Sources</FieldLabel>
              <InputText
                value={form.leadSource}
                onChange={(e) => set('leadSource', e.target.value)}
                placeholder="Type and/or choose"
                className="masterCert__input"
                style={{ width: '100%' }}
              />
            </Field>

            <Field>
              <FieldLabel>Finance Company</FieldLabel>
              <InputText
                value={form.financeCompanyName}
                onChange={(e) => set('financeCompanyName', e.target.value)}
                placeholder="Type and/or choose"
                className="masterCert__input"
                style={{ width: '100%' }}
              />
            </Field>

            <Field span2>
              <FieldLabel>Description</FieldLabel>
              <InputTextarea
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                rows={3}
                autoResize
                placeholder="Enter description..."
                style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
              />
            </Field>
          </div>
        </section> */}

      </div>

      <CreateNewCarriers
        open={showCreateCarrier}
        onClose={() => setShowCreateCarrier(false)}
        onSaved={() => setShowCreateCarrier(false)}
      />

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <div className="masterCert__footer" style={{ borderTop: '1px solid #e6e7ef', paddingTop: 14, marginTop: 0, justifyContent: 'flex-end' }}>
        <Button
          label="Cancel"
          className="p-button-text appButton--secondary"
          onClick={() => navigate(-1)}
          disabled={saving}
        />
        <Button
          label={saving ? 'Creating…' : 'Create Policy'}
          icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
          className="masterCert__btnPrimary"
          onClick={handleSave}
          disabled={saving}
        />
      </div>
    </div>
  );
}
